import ReactAnd from 'react-and';
import { Button } from '@components/Clickables/Buttons/Button';
import { InternalLink } from '@components/Clickables/InternalLink/InternalLink';
import { IFAQData } from '@components/FAQ/FAQ';
import { getFishingWaterUrl, getRegionUrl, getSpeciesUrl } from '@domain/routes/urls';
import { CountryDetailFields } from '@generated/graphql';
import { addDefiniteArticleToCountryName } from '@domain/country/addDefiniteArticleToCountryName';
import { formatList } from '@lib/strings/formatList';
import { IRecentCatchesSummary } from './buildRecentCatchesSummary';

interface IProps {
  country: CountryDetailFields | null;
  handleCTAClick: () => void;
  topSpecies?: { _id: string; displayName: string; url: string }[];
  topFishingWaters?: { _id: string; displayName: string }[];
  topStatesByCatches?: { stateId: string; stateName: string; totalCatchesThisYear: string }[];
  recentCatchesSummary?: IRecentCatchesSummary;
  hasRegulations?: boolean;
}

const NUMBER_OF_FISHING_WATERS_TO_SHOW = 3;
const NUMBER_OF_FISHING_SPECIES_TO_SHOW = 3;
const NUMBER_OF_CATCH_REGIONS_TO_SHOW = 5;
// "What's biting" entry only renders with enough variety to read naturally.
const MIN_RECENT_SPECIES = 3;
const MIN_RECENT_WATERS = 2;
const NUMBER_OF_RECENT_SPECIES_TO_SHOW = 3;
const NUMBER_OF_RECENT_WATERS_TO_SHOW = 2;

export const getFAQData = ({
  country,
  handleCTAClick,
  topSpecies,
  topFishingWaters,
  topStatesByCatches,
  recentCatchesSummary,
  hasRegulations,
}: IProps): IFAQData[] => {
  const countryName = country?.countryName;
  const countryId = country?.id;

  const countryNameWithDefiniteArticle =
    countryName && countryId ? addDefiniteArticleToCountryName(countryName, countryId) : undefined;

  const topCatchRegions = (topStatesByCatches ?? [])
    .slice(0, NUMBER_OF_CATCH_REGIONS_TO_SHOW)
    .map(state => ({
      stateName: state.stateName,
      url:
        countryId && countryName
          ? getRegionUrl(countryId, state.stateId, countryName, state.stateName)
          : undefined,
    }));

  const recentSpecies = recentCatchesSummary?.species ?? [];
  const recentWaters = recentCatchesSummary?.waters ?? [];
  const hasRecentCatches =
    recentSpecies.length >= MIN_RECENT_SPECIES && recentWaters.length >= MIN_RECENT_WATERS;

  return [
    ...(hasRegulations
      ? [
          {
            id: '1',
            title: `📜 What are the fishing regulations in ${countryNameWithDefiniteArticle}?`,
            body: (
              <>
                {`Fishing regulations in ${countryNameWithDefiniteArticle} vary by location, species, and season. Rules may include size limits, bag limits, licensing requirements, and protected areas. Because regulations are managed locally and can change throughout the year, it's important to check the latest official guidance before fishing.`}
                <br />
                <br />
                {`To stay up to date and avoid guesswork, use the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` to explore local rules, discover popular waters, and see what other anglers are catching across ${countryNameWithDefiniteArticle}.`}
              </>
            ),
            bodyString: `Fishing regulations in ${countryNameWithDefiniteArticle} vary by region, species, and season. Rules may include size limits, bag limits, licensing requirements, and protected areas. Because regulations are managed locally and can change throughout the year, it's important to check the latest official guidance before fishing. To stay up to date and avoid guesswork, use the Fishbrain app to explore local rules, discover popular waters, and see what other anglers are catching across ${countryNameWithDefiniteArticle}.`,
          },
        ]
      : []),
    ...(topSpecies && topSpecies.length > 0
      ? [
          {
            id: '2',
            title: `🐟 What are the best fish species to catch in ${countryNameWithDefiniteArticle}?`,
            body: (
              <>
                {`Some of the most popular species anglers target in ${countryNameWithDefiniteArticle} include `}
                <ReactAnd>
                  {topSpecies
                    .slice(0, NUMBER_OF_FISHING_SPECIES_TO_SHOW)
                    .map(({ displayName, _id, url }) => {
                      return (
                        <InternalLink
                          key={`species-${_id}`}
                          to={url}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {`. The best species often depend on season, water type, and location within the country.`}
                <br />
                <br />
                {`For more detailed, real-time insights, the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` shows which species are being caught near you and what baits are working best across ${countryNameWithDefiniteArticle}.`}
              </>
            ),
            bodyString: `Some of the most popular species anglers target in ${countryNameWithDefiniteArticle} include ${topSpecies
              .slice(0, NUMBER_OF_FISHING_SPECIES_TO_SHOW)
              .map(({ displayName, url }) => `<a href="${url}">${displayName}</a>`)
              .join(
                ', ',
              )}. The best species often depend on season, water type, and location within the country. For more detailed, real-time insights, the Fishbrain app shows which species are being caught near you and what baits are working best across ${countryNameWithDefiniteArticle}.`,
          },
        ]
      : []),
    ...(topFishingWaters && topFishingWaters.length > 0
      ? [
          {
            id: '3',
            title: `🌊 Where are the top fishing spots in ${countryNameWithDefiniteArticle}?`,
            body: (
              <>
                {`Some of the most popular fishing spots in ${countryNameWithDefiniteArticle} include `}
                <ReactAnd>
                  {topFishingWaters
                    .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
                    .flatMap(({ displayName, _id }) => {
                      return (
                        <InternalLink
                          key={`fishing-water-${_id}`}
                          to={getFishingWaterUrl(_id, displayName)}
                          className="body-text-sm text-primary"
                        >
                          {displayName}
                        </InternalLink>
                      );
                    })}
                </ReactAnd>
                {`. Anglers across the country fish a wide range of waters, with opportunities varying by region and season.`}
                <br />
                <br />
                {`With the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {`, you can explore local hotspots anywhere in ${countryNameWithDefiniteArticle}, see recent catches, and find out what's working near you.`}
              </>
            ),
            bodyString: `Some of the most popular fishing spots in ${countryNameWithDefiniteArticle} include ${topFishingWaters
              .slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW)
              .flatMap(({ displayName, _id }) => {
                return `<a href="${getFishingWaterUrl(_id, displayName)}">${displayName}</a>`;
              })
              .join(
                ', ',
              )}. Anglers across the country fish a wide range of waters, with opportunities varying by region and season. With the Fishbrain app, you can explore local hotspots anywhere in ${countryNameWithDefiniteArticle}, see recent catches, and find out what's working near you.`,
          },
        ]
      : []),
    ...(topCatchRegions.length > 0
      ? [
          {
            id: '4',
            title: `🗺️ Which regions in ${countryNameWithDefiniteArticle} have the best fishing?`,
            body: (
              <>
                {`Based on catches logged by the Fishbrain community, the top regions for fishing in ${countryNameWithDefiniteArticle} are `}
                <ReactAnd>
                  {topCatchRegions.map(({ stateName, url }) =>
                    url ? (
                      <InternalLink
                        key={`catch-region-${stateName}`}
                        to={url}
                        className="body-text-sm text-primary"
                      >
                        {stateName}
                      </InternalLink>
                    ) : (
                      <span key={`catch-region-${stateName}`}>{stateName}</span>
                    ),
                  )}
                </ReactAnd>
                {'.'}
              </>
            ),
            bodyString: `Based on catches logged by the Fishbrain community, the top regions for fishing in ${countryNameWithDefiniteArticle} are ${formatList(
              topCatchRegions.map(({ stateName, url }) =>
                url ? `<a href="${url}">${stateName}</a>` : stateName,
              ),
            )}.`,
          },
        ]
      : []),
    ...(hasRecentCatches
      ? [
          {
            id: '5',
            title: `🎣 What are anglers currently catching in ${countryNameWithDefiniteArticle}?`,
            body: (
              <>
                {`Recent catches logged in ${countryNameWithDefiniteArticle} include `}
                <ReactAnd>
                  {recentSpecies.slice(0, NUMBER_OF_RECENT_SPECIES_TO_SHOW).map(({ id, name }) => (
                    <InternalLink
                      key={`recent-species-${id}`}
                      to={getSpeciesUrl(id, name)}
                      className="body-text-sm text-primary"
                    >
                      {name}
                    </InternalLink>
                  ))}
                </ReactAnd>
                {`, with active reports coming in from waters like `}
                <ReactAnd>
                  {recentWaters.slice(0, NUMBER_OF_RECENT_WATERS_TO_SHOW).map(({ id, name }) => (
                    <InternalLink
                      key={`recent-water-${id}`}
                      to={getFishingWaterUrl(id, name)}
                      className="body-text-sm text-primary"
                    >
                      {name}
                    </InternalLink>
                  ))}
                </ReactAnd>
                {`. What's biting changes week to week depending on weather, water temperature, and season.`}
                <br />
                <br />
                {`Open the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` to see the latest reports from ${countryNameWithDefiniteArticle} in real time — including bait, conditions, and exact locations.`}
              </>
            ),
            bodyString: `Recent catches logged in ${countryNameWithDefiniteArticle} include ${recentSpecies
              .slice(0, NUMBER_OF_RECENT_SPECIES_TO_SHOW)
              .map(({ id, name }) => `<a href="${getSpeciesUrl(id, name)}">${name}</a>`)
              .join(', ')}, with active reports coming in from waters like ${recentWaters
              .slice(0, NUMBER_OF_RECENT_WATERS_TO_SHOW)
              .map(({ id, name }) => `<a href="${getFishingWaterUrl(id, name)}">${name}</a>`)
              .join(
                ' and ',
              )}. What's biting changes week to week depending on weather, water temperature, and season. Open the Fishbrain app to see the latest reports from ${countryNameWithDefiniteArticle} in real time — including bait, conditions, and exact locations.`,
          },
        ]
      : []),
  ];
};
